import { useState } from 'react';

const faqs = [
  {
    q: 'What is Hipobuy Spreadsheet?',
    a: 'Hipobuy Spreadsheet is a comprehensive product research system that organizes thousands of products into categorized lists. It helps shoppers quickly find, compare, and evaluate products across multiple categories including Shoes, Hoodies, T-Shirts, Jackets, Accessories, and more. The spreadsheet format makes it easy to browse, sort, and filter items based on your preferences.'
  },
  {
    q: 'How do I use Hipobuy Spreadsheet to find products?',
    a: 'Simply browse through our organized category directory above. Each category links directly to a curated spreadsheet view on our main website where you can see all products listed with details. You can filter by type, compare items side by side, and access direct links to each product for detailed information.'
  },
  {
    q: 'Is Hipobuy Spreadsheet free to use?',
    a: 'Yes! Hipobuy Spreadsheet resources are completely free to access. Our goal is to provide the most comprehensive and up-to-date product research tools available. All category directories, comparison guides, and educational articles are freely accessible without any subscription required.'
  },
  {
    q: 'How often is the Hipobuy Spreadsheet updated?',
    a: 'Our product database and spreadsheet resources are updated regularly to ensure you always have access to the latest products and information. We continuously monitor product availability, add new items, and remove outdated listings to maintain the highest quality resource for our users.'
  },
  {
    q: 'Is it safe to shop using Hipobuy Spreadsheet?',
    a: 'Absolutely. Hipobuy Spreadsheet is designed with safety as a top priority. We provide educational guides on safe shopping practices, verify product sources, and maintain transparent listings. Our safety guides cover everything from recognizing legitimate sellers to protecting your personal information during transactions.'
  },
  {
    q: 'How does Hipobuy Spreadsheet compare to other spreadsheet tools?',
    a: 'Hipobuy Spreadsheet stands out for its comprehensive category coverage, user-friendly organization, and regularly updated product database. Unlike other tools that may focus on a single category or remain static, our spreadsheet covers 14+ product categories with fresh updates. Check our comparison guides for detailed breakdowns against Kakobuy, CSSBuy, Superbuy and Oopbuy spreadsheets.'
  }
];

const testimonials = [
  { name: 'Marcus Reynolds', handle: '@marcusfinds', text: 'The Hipobuy Spreadsheet completely changed how I discover products. The category organization is so intuitive and the guides saved me hours of research. Highly recommend for anyone into fashion shopping.' },
  { name: 'Elena Vasquez', handle: '@elenastyle', text: 'I was overwhelmed by all the options out there until I found Hipobuy Spreadsheet. The comparison guides between different spreadsheet tools are incredibly detailed and helped me choose the right resource for my needs.' },
  { name: 'James Thornton', handle: '@jthornton', text: 'What sets Hipobuy Spreadsheet apart is the safety education. As someone new to spreadsheet-based shopping, the guides gave me confidence. Now I browse and compare products with ease.' },
  { name: 'Sarah Mitchell', handle: '@sarahm_research', text: 'The product database is so comprehensive. I use it daily to find new items across Shoes, Hoodies and Accessories. The updated lists ensure I never miss trending products.' },
  { name: 'David Chen', handle: '@dchen_finds', text: 'After trying multiple spreadsheet tools including Kakobuy and CSSBuy, Hipobuy Spreadsheet has the best organization and most frequent updates. The comparison page sealed the deal for me.' },
  { name: 'Olivia Park', handle: '@oliviap_style', text: 'I love how the Hipobuy Spreadsheet breaks everything down by category. The Shoes section alone has saved me countless hours. Plus the buying guides are written in plain English that anyone can understand.' },
];

export default function TestimonialsFAQ() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <section id="faq" className="bg-background-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-10 max-w-[1280px] mx-auto">
        <div className="mb-14">
          <span className="inline-block px-3 py-1 bg-secondary-100 rounded-full text-secondary-700 text-xs font-medium tracking-wider">
            Community
          </span>
          <h2 className="mt-4 text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight">
            What Our Users Say
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-20">
          {[0, 1, 2].map((colIdx) => (
            <div key={colIdx} className="flex flex-col gap-5">
              {testimonials.filter((_, i) => i % 3 === colIdx).map((t) => (
                <div key={t.handle} className="bg-background-50 rounded-2xl p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-secondary-600 font-semibold text-sm">
                        {t.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <p className="text-foreground-900 text-sm font-semibold">{t.name}</p>
                      <p className="text-foreground-400 text-xs">{t.handle}</p>
                    </div>
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    &ldquo;{t.text}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div>
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 bg-accent-100/80 rounded-full text-accent-700 text-xs font-medium tracking-wider mb-3">
              FAQ
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
              Frequently Asked Questions About <span className="text-accent-600">Hipobuy Spreadsheet</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-background-50 rounded-xl border border-background-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                >
                  <h4 className="text-foreground-900 text-sm md:text-base font-medium pr-4">
                    <a id={`faq-${idx}`} href={`#faq-${idx}`} className="no-underline">{faq.q}</a>
                  </h4>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === idx ? 'ri-subtract-line' : 'ri-add-line'} text-foreground-500 text-lg`}></i>
                  </div>
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-4">
                    <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}